"use client";

import { msg } from "@lingui/core/macro";
import { type ComponentType, lazy } from "react";
import { ACTIVATE_WORKSPACE_WORKFLOW_SKILL_NAME } from "../activate-workspace-workflow-skill/constants";
import { ADD_SEARCH_CONSOLE_PROPERTY_NAME } from "../add-search-console-property";
import { AddSearchConsolePropertyConfirm } from "../add-search-console-property/ui";
import {
  CREATE_AI_ARTIFACT_NAME,
  UPDATE_AI_ARTIFACT_NAME,
} from "../ai-artifact-shared/constants";
import { parseAiArtifactSavedToolOutput } from "../ai-artifact-shared/output";
import { AiArtifactSavedToolOutput } from "../ai-artifact-shared/ui";
import { CREATE_DASHBOARD_NAME } from "../create-dashboard";
import { CreateDashboardConfirm } from "../create-dashboard/ui";
import { CREATE_DASHBOARD_ITEM_NAME } from "../create-dashboard-item";
import { CreateDashboardItemConfirm } from "../create-dashboard-item/ui";
import { CREATE_INDEX_WORKER_NAME } from "../create-index-worker";
import { CreateIndexWorkerConfirm } from "../create-index-worker/ui";
import { CREATE_PROJECT_NAME } from "../create-project";
import { CreateProjectConfirm } from "../create-project/ui";
import { CREATE_SA_BIGQUERY_NAME } from "../create-search-analytics-bigquery";
import { CreateSearchAnalyticsBigQueryConfirm } from "../create-search-analytics-bigquery/ui";
import { CREATE_SA_QUERY_GROUP_NAME } from "../create-search-analytics-query-group";
import { CreateSaQueryGroupConfirm } from "../create-search-analytics-query-group/ui";
import { CREATE_SA_URL_GROUP_NAME } from "../create-search-analytics-url-group";
import { CreateSaUrlGroupConfirm } from "../create-search-analytics-url-group/ui";
import { CREATE_SA_VIEW_NAME } from "../create-search-analytics-view";
import { CreateSearchAnalyticsViewConfirm } from "../create-search-analytics-view/ui";
import { CREATE_UA_BIGQUERY_NAME } from "../create-user-analytics-bigquery";
import { CreateUserAnalyticsBigQueryConfirm } from "../create-user-analytics-bigquery/ui";
import { CREATE_UA_URL_GROUP_NAME } from "../create-user-analytics-url-group";
import { CreateUaUrlGroupConfirm } from "../create-user-analytics-url-group/ui";
import { CREATE_UA_VIEW_NAME } from "../create-user-analytics-view";
import { CreateUserAnalyticsViewConfirm } from "../create-user-analytics-view/ui";
import { CREATE_WATCH_RULE_NAME } from "../create-watch-rule/constants";
import { DEACTIVATE_WORKSPACE_WORKFLOW_SKILL_NAME } from "../deactivate-workspace-workflow-skill/constants";
import { DELETE_DASHBOARD_NAME } from "../delete-dashboard";
import { DeleteDashboardConfirm } from "../delete-dashboard/ui";
import { DELETE_DASHBOARD_ITEM_NAME } from "../delete-dashboard-item";
import { DeleteDashboardItemConfirm } from "../delete-dashboard-item/ui";
import { DELETE_INDEX_WORKER_NAME } from "../delete-index-worker";
import { DeleteIndexWorkerConfirm } from "../delete-index-worker/ui";
import { DELETE_PROJECT_NAME } from "../delete-project";
import { DeleteProjectConfirm } from "../delete-project/ui";
import { DELETE_SA_QUERY_GROUP_NAME } from "../delete-search-analytics-query-group";
import { DeleteSearchAnalyticsQueryGroupConfirm } from "../delete-search-analytics-query-group/ui";
import { DELETE_SA_URL_GROUP_NAME } from "../delete-search-analytics-url-group";
import { DeleteSearchAnalyticsUrlGroupConfirm } from "../delete-search-analytics-url-group/ui";
import { DELETE_SA_VIEW_NAME } from "../delete-search-analytics-view";
import { DeleteSearchAnalyticsViewConfirm } from "../delete-search-analytics-view/ui";
import { DELETE_SEARCH_CONSOLE_PROPERTY_NAME } from "../delete-search-console-property";
import { DeleteSearchConsolePropertyConfirm } from "../delete-search-console-property/ui";
import { DELETE_SERVICE_ACCOUNT_NAME } from "../delete-service-account/constants";
import { DELETE_UA_URL_GROUP_NAME } from "../delete-user-analytics-url-group";
import { DeleteUserAnalyticsUrlGroupConfirm } from "../delete-user-analytics-url-group/ui";
import { DELETE_UA_VIEW_NAME } from "../delete-user-analytics-view";
import { DeleteUserAnalyticsViewConfirm } from "../delete-user-analytics-view/ui";
import { DELETE_WATCH_RULE_NAME } from "../delete-watch-rule/constants";
import { DISABLE_WATCH_RULE_NAME } from "../disable-watch-rule/constants";
import { DRAFT_WORKSPACE_WORKFLOW_SKILL_NAME } from "../draft-workspace-workflow-skill/constants";
import { DUPLICATE_INDEX_WORKER_NAME } from "../duplicate-index-worker";
import { DuplicateIndexWorkerConfirm } from "../duplicate-index-worker/ui";
import { DUPLICATE_PROJECT_NAME } from "../duplicate-project";
import { DuplicateProjectConfirm } from "../duplicate-project/ui";
import { EDIT_DASHBOARD_NAME } from "../edit-dashboard";
import { EditDashboardConfirm } from "../edit-dashboard/ui";
import { EDIT_DASHBOARD_ITEM_NAME } from "../edit-dashboard-item";
import { EditDashboardItemConfirm } from "../edit-dashboard-item/ui";
import { EDIT_INDEX_WORKER_NAME } from "../edit-index-worker";
import { EditIndexWorkerConfirm } from "../edit-index-worker/ui";
import { EDIT_PROJECT_NAME } from "../edit-project";
import { EditProjectConfirm } from "../edit-project/ui";
import { EDIT_USER_ROLE_NAME } from "../edit-user-role/constants";
import { EDIT_WORKSPACE_NAME } from "../edit-workspace/constants";
import { ENABLE_WATCH_RULE_NAME } from "../enable-watch-rule/constants";
import { GET_INDEX_WORKER_EXPORT_DATA_NAME } from "../get-index-worker-export-data/constants";
import { parseIndexWorkerExportToolOutput } from "../get-index-worker-export-data/output";
import { GetIndexWorkerExportDataToolOutput } from "../get-index-worker-export-data/ui";
import { IMPORT_WORKSPACE_WORKFLOW_SKILL_NAME } from "../import-workspace-workflow-skill/constants";
import { INVITE_USER_NAME } from "../invite-user/constants";
import { MOVE_INDEX_WORKER_PROJECT_NAME } from "../move-index-worker-project";
import { MoveIndexWorkerProjectConfirm } from "../move-index-worker-project/ui";
import { REGISTER_SERVICE_ACCOUNT_NAME } from "../register-service-account";
import { RegisterSearchConsoleServiceAccountConfirm } from "../register-service-account/ui";
import { REMEMBER_WORKSPACE_MEMORY_NAME } from "../remember-workspace-memory/constants";
import { REMOVE_USER_FROM_WORKSPACE_NAME } from "../remove-user-from-workspace/constants";
import { RERUN_INDEX_WORKER_JOB_NAME } from "../rerun-index-worker-job";
import { RerunIndexWorkerJobConfirm } from "../rerun-index-worker-job/ui";
import { UPDATE_NOTIFICATION_SETTING_NAME } from "../update-notification-setting/constants";

import { UPDATE_QUERY_GROUP_NAME } from "../update-query-group";
import { UpdateQueryGroupConfirm } from "../update-query-group/ui";
import { UPDATE_QUERY_GROUP_ORDER_NAME } from "../update-query-group-order";
import { UpdateQueryGroupOrderConfirm } from "../update-query-group-order/ui";
import { UPDATE_SA_DAILY_EXPORT_SETTING_NAME } from "../update-search-analytics-daily-export-setting";
import { UpdateSaDailyExportSettingConfirm } from "../update-search-analytics-daily-export-setting/ui";
import { UPDATE_SA_URL_GROUP_NAME } from "../update-search-analytics-url-group";
import { UpdateSearchAnalyticsUrlGroupConfirm } from "../update-search-analytics-url-group/ui";
import { UPDATE_SA_VIEW_NAME } from "../update-search-analytics-view";
import { UpdateSearchAnalyticsViewConfirm } from "../update-search-analytics-view/ui";
import { UPDATE_URL_GROUP_ORDER_NAME } from "../update-url-group-order";
import { UpdateUrlGroupOrderConfirm } from "../update-url-group-order/ui";
import { UPDATE_UA_BIGQUERY_EVENT_PARAM_KEYS_NAME } from "../update-user-analytics-bigquery-event-param-keys";
import { UpdateUserAnalyticsBigQueryEventParamKeysConfirm } from "../update-user-analytics-bigquery-event-param-keys/ui";
import { UPDATE_UA_KEY_EVENTS_NAME } from "../update-user-analytics-key-events";
import { UpdateUserAnalyticsKeyEventsConfirm } from "../update-user-analytics-key-events/ui";
import { UPDATE_UA_URL_GROUP_NAME } from "../update-user-analytics-url-group";
import { UpdateUserAnalyticsUrlGroupConfirm } from "../update-user-analytics-url-group/ui";
import { UPDATE_UA_VIEW_NAME } from "../update-user-analytics-view";
import { UpdateUserAnalyticsViewConfirm } from "../update-user-analytics-view/ui";
import { UPDATE_WATCH_RULE_NAME } from "../update-watch-rule/constants";
import type {
  ConfirmProps,
  ToolApprovalReviewLoader,
  ToolApprovalReviewRegistration,
  ToolOutputRegistration,
} from "./types";

/** legacy confirmation component を Tool 名で引く registry。 */
export type ToolConfirmRegistry = Record<string, ComponentType<ConfirmProps>>;
type AnyToolOutputRegistration = ToolOutputRegistration<unknown>;
/** Tool 固有の完了結果 parser / renderer を Tool 名で引く registry。 */
export type ToolOutputRegistry = Record<string, AnyToolOutputRegistration>;
/** native approval の review と action 表示を Tool 名で引く registry。 */
export type ToolApprovalReviewRegistry = Record<
  string,
  ToolApprovalReviewRegistration
>;

const CONFIRM_UI_REGISTRY = new Map<string, ComponentType<ConfirmProps>>();
const OUTPUT_UI_REGISTRY = new Map<string, AnyToolOutputRegistration>();
const APPROVAL_REVIEW_REGISTRY = new Map<
  string,
  ToolApprovalReviewRegistration
>();

/**
 * 登録済みの確認UI（Confirmation）の一覧を返します。
 * - キーはツール名、値は Confirm コンポーネントです。
 */
export function getToolConfirmRegistry(): ToolConfirmRegistry {
  return Object.fromEntries(CONFIRM_UI_REGISTRY.entries());
}

/** Tool 名を key として、登録済みの custom output renderer をすべて返す。 */
export function getToolOutputRegistry(): ToolOutputRegistry {
  return Object.fromEntries(OUTPUT_UI_REGISTRY.entries());
}

/**
 * Native approval の request 内へ表示する Tool 固有 review を返します。
 *
 * Tool ごとの登録は後続の vertical slice で追加します。これにより共通 chat
 * wiring を先に deploy しても、未移行 Tool の legacy confirmation を維持できます。
 */
export function getToolApprovalReviewRegistry(): ToolApprovalReviewRegistry {
  return Object.fromEntries(APPROVAL_REVIEW_REGISTRY.entries());
}

/** Tool 固有 review を module 初期化時に lazy component として固定します。 */
function registerToolApprovalReview(
  name: string,
  registration: Omit<ToolApprovalReviewRegistration, "component"> & {
    loader: ToolApprovalReviewLoader;
  },
) {
  const { loader, ...options } = registration;
  APPROVAL_REVIEW_REGISTRY.set(name, {
    component: lazy(loader),
    ...options,
  });
}

/**
 * 確認UI（Confirmation）コンポーネントを登録します。
 * - ツール名（name）と Confirm コンポーネントの対応づけを行います。
 * - 実行時にチャット UI から `getToolConfirmRegistry()` 経由で参照されます。
 */
function registerUIConfirm(
  name: string,
  component: ComponentType<ConfirmProps>,
) {
  CONFIRM_UI_REGISTRY.set(name, component);
}

/** Tool の custom output parser と renderer の組を登録する。 */
function registerUIOutput<TParsedOutput>(
  name: string,
  registration: ToolOutputRegistration<TParsedOutput>,
) {
  OUTPUT_UI_REGISTRY.set(name, registration as AnyToolOutputRegistration);
}

registerToolApprovalReview(EDIT_USER_ROLE_NAME, {
  loader: () => import("../edit-user-role/ui"),
  approveLabel: msg`Update role`,
  denyLabel: msg`Cancel`,
  refreshRouteOnOutput: true,
});

registerToolApprovalReview(EDIT_WORKSPACE_NAME, {
  loader: () => import("../edit-workspace/ui"),
  approveLabel: msg`Update`,
  denyLabel: msg`Cancel`,
  refreshRouteOnOutput: true,
});

registerToolApprovalReview(INVITE_USER_NAME, {
  loader: () => import("../invite-user/ui"),
  approveLabel: msg`Invite`,
  denyLabel: msg`Cancel`,
  refreshRouteOnOutput: true,
});

registerToolApprovalReview(REMOVE_USER_FROM_WORKSPACE_NAME, {
  loader: () => import("../remove-user-from-workspace/ui"),
  approveLabel: msg`Remove`,
  approveVariant: "destructive",
  denyLabel: msg`Cancel`,
  refreshRouteOnOutput: true,
});

registerToolApprovalReview(DELETE_SERVICE_ACCOUNT_NAME, {
  loader: () => import("../delete-service-account/ui"),
  approveLabel: msg`Unregister`,
  approveVariant: "destructive",
  denyLabel: msg`Cancel`,
  refreshRouteOnOutput: true,
});

registerToolApprovalReview(UPDATE_NOTIFICATION_SETTING_NAME, {
  loader: () => import("../update-notification-setting/ui"),
  approveLabel: msg`Update`,
  denyLabel: msg`Cancel`,
  refreshRouteOnOutput: true,
});

registerToolApprovalReview(DRAFT_WORKSPACE_WORKFLOW_SKILL_NAME, {
  loader: () => import("../draft-workspace-workflow-skill/ui"),
  approveLabel: msg`Create draft`,
  denyLabel: msg`Cancel`,
});

registerToolApprovalReview(IMPORT_WORKSPACE_WORKFLOW_SKILL_NAME, {
  loader: () => import("../import-workspace-workflow-skill/ui"),
  approveLabel: msg`Import draft`,
  denyLabel: msg`Cancel`,
});

registerToolApprovalReview(ACTIVATE_WORKSPACE_WORKFLOW_SKILL_NAME, {
  loader: () => import("../activate-workspace-workflow-skill/ui"),
  approveLabel: msg`Activate`,
  denyLabel: msg`Cancel`,
});

registerToolApprovalReview(DEACTIVATE_WORKSPACE_WORKFLOW_SKILL_NAME, {
  loader: () => import("../deactivate-workspace-workflow-skill/ui"),
  approveLabel: msg`Deactivate`,
  denyLabel: msg`Cancel`,
});

registerToolApprovalReview(REMEMBER_WORKSPACE_MEMORY_NAME, {
  loader: () => import("../remember-workspace-memory/ui"),
  approveLabel: msg`Save`,
  denyLabel: msg`Cancel`,
});

registerToolApprovalReview(CREATE_WATCH_RULE_NAME, {
  loader: () => import("../create-watch-rule/approval-ui"),
  approveLabel: msg`Create rule`,
  denyLabel: msg`Cancel`,
  refreshRouteOnOutput: true,
});

registerToolApprovalReview(UPDATE_WATCH_RULE_NAME, {
  loader: () => import("../update-watch-rule/approval-ui"),
  approveLabel: msg`Update rule`,
  denyLabel: msg`Cancel`,
  refreshRouteOnOutput: true,
});

registerToolApprovalReview(ENABLE_WATCH_RULE_NAME, {
  loader: () => import("../enable-watch-rule/approval-ui"),
  approveLabel: msg`Enable rule`,
  denyLabel: msg`Cancel`,
  refreshRouteOnOutput: true,
});

registerToolApprovalReview(DISABLE_WATCH_RULE_NAME, {
  loader: () => import("../disable-watch-rule/approval-ui"),
  approveLabel: msg`Disable rule`,
  denyLabel: msg`Cancel`,
  refreshRouteOnOutput: true,
});

registerToolApprovalReview(DELETE_WATCH_RULE_NAME, {
  loader: () => import("../delete-watch-rule/approval-ui"),
  approveLabel: msg`Delete rule`,
  approveVariant: "destructive",
  denyLabel: msg`Cancel`,
  refreshRouteOnOutput: true,
});

registerUIConfirm(CREATE_PROJECT_NAME, CreateProjectConfirm);
registerUIConfirm(CREATE_INDEX_WORKER_NAME, CreateIndexWorkerConfirm);
registerUIConfirm(CREATE_DASHBOARD_ITEM_NAME, CreateDashboardItemConfirm);
registerUIConfirm(CREATE_DASHBOARD_NAME, CreateDashboardConfirm);
registerUIConfirm(DUPLICATE_INDEX_WORKER_NAME, DuplicateIndexWorkerConfirm);
registerUIConfirm(DUPLICATE_PROJECT_NAME, DuplicateProjectConfirm);
registerUIConfirm(
  MOVE_INDEX_WORKER_PROJECT_NAME,
  MoveIndexWorkerProjectConfirm,
);
registerUIConfirm(DELETE_PROJECT_NAME, DeleteProjectConfirm);
registerUIConfirm(
  DELETE_SEARCH_CONSOLE_PROPERTY_NAME,
  DeleteSearchConsolePropertyConfirm,
);
registerUIConfirm(DELETE_INDEX_WORKER_NAME, DeleteIndexWorkerConfirm);
registerUIConfirm(EDIT_DASHBOARD_ITEM_NAME, EditDashboardItemConfirm);
registerUIConfirm(DELETE_DASHBOARD_NAME, DeleteDashboardConfirm);
registerUIConfirm(DELETE_DASHBOARD_ITEM_NAME, DeleteDashboardItemConfirm);
registerUIConfirm(EDIT_PROJECT_NAME, EditProjectConfirm);
registerUIConfirm(EDIT_INDEX_WORKER_NAME, EditIndexWorkerConfirm);
registerUIConfirm(EDIT_DASHBOARD_NAME, EditDashboardConfirm);
registerUIConfirm(CREATE_SA_VIEW_NAME, CreateSearchAnalyticsViewConfirm);
registerUIConfirm(
  CREATE_SA_BIGQUERY_NAME,
  CreateSearchAnalyticsBigQueryConfirm,
);
registerUIConfirm(CREATE_SA_QUERY_GROUP_NAME, CreateSaQueryGroupConfirm);
registerUIConfirm(CREATE_UA_VIEW_NAME, CreateUserAnalyticsViewConfirm);
registerUIConfirm(CREATE_UA_BIGQUERY_NAME, CreateUserAnalyticsBigQueryConfirm);
registerUIConfirm(CREATE_UA_URL_GROUP_NAME, CreateUaUrlGroupConfirm);
registerUIConfirm(
  REGISTER_SERVICE_ACCOUNT_NAME,
  RegisterSearchConsoleServiceAccountConfirm,
);
registerUIConfirm(RERUN_INDEX_WORKER_JOB_NAME, RerunIndexWorkerJobConfirm);
registerUIConfirm(
  ADD_SEARCH_CONSOLE_PROPERTY_NAME,
  AddSearchConsolePropertyConfirm,
);
registerUIConfirm(
  DELETE_SA_QUERY_GROUP_NAME,
  DeleteSearchAnalyticsQueryGroupConfirm,
);
registerUIConfirm(CREATE_SA_URL_GROUP_NAME, CreateSaUrlGroupConfirm);
registerUIConfirm(DELETE_SA_VIEW_NAME, DeleteSearchAnalyticsViewConfirm);
registerUIConfirm(
  UPDATE_SA_URL_GROUP_NAME,
  UpdateSearchAnalyticsUrlGroupConfirm,
);
registerUIConfirm(
  UPDATE_SA_DAILY_EXPORT_SETTING_NAME,
  UpdateSaDailyExportSettingConfirm,
);
registerUIConfirm(UPDATE_SA_VIEW_NAME, UpdateSearchAnalyticsViewConfirm);
registerUIConfirm(UPDATE_QUERY_GROUP_NAME, UpdateQueryGroupConfirm);
registerUIConfirm(UPDATE_QUERY_GROUP_ORDER_NAME, UpdateQueryGroupOrderConfirm);
registerUIConfirm(
  UPDATE_UA_KEY_EVENTS_NAME,
  UpdateUserAnalyticsKeyEventsConfirm,
);
registerUIConfirm(
  UPDATE_UA_BIGQUERY_EVENT_PARAM_KEYS_NAME,
  UpdateUserAnalyticsBigQueryEventParamKeysConfirm,
);
registerUIConfirm(UPDATE_UA_URL_GROUP_NAME, UpdateUserAnalyticsUrlGroupConfirm);
registerUIConfirm(UPDATE_UA_VIEW_NAME, UpdateUserAnalyticsViewConfirm);

registerUIConfirm(DELETE_UA_VIEW_NAME, DeleteUserAnalyticsViewConfirm);
registerUIConfirm(DELETE_UA_URL_GROUP_NAME, DeleteUserAnalyticsUrlGroupConfirm);
registerUIConfirm(
  DELETE_SA_URL_GROUP_NAME,
  DeleteSearchAnalyticsUrlGroupConfirm,
);
registerUIConfirm(UPDATE_URL_GROUP_ORDER_NAME, UpdateUrlGroupOrderConfirm);

registerUIOutput(GET_INDEX_WORKER_EXPORT_DATA_NAME, {
  parse: parseIndexWorkerExportToolOutput,
  component: GetIndexWorkerExportDataToolOutput,
});

registerUIOutput(CREATE_AI_ARTIFACT_NAME, {
  parse: parseAiArtifactSavedToolOutput,
  component: AiArtifactSavedToolOutput,
});

registerUIOutput(UPDATE_AI_ARTIFACT_NAME, {
  parse: parseAiArtifactSavedToolOutput,
  component: AiArtifactSavedToolOutput,
});
